import { useState, useRef, useEffect } from 'react';

const DEFAULT_COLORS = [
  '#E8F7FD', '#D1EEFA', '#BAE6F8', '#A3DEF5', '#8CD6F3', '#75CDF0', '#5DC5EE', '#4FBFEC', '#46BDEC', '#2FB5E9',
  '#F2EDF7', '#E5DCEF', '#D7CAE8', '#CAB8E0', '#BDA6D8', '#B095D0', '#A283C9', '#9571C1', '#885FB9', '#7B4EB1',
  '#FCE8F1', '#F9D2E4', '#F7BBD6', '#F4A4C8', '#F18EBA', '#EE77AD', '#EB609F', '#E94B93', '#E63383', '#E31C76',
  '#FCE8E8', '#F9D2D2', '#F7BBBB', '#F4A4A4', '#F18E8E', '#EE7777', '#EB6060', '#E94949', '#E63333', '#E31C1C',
  '#FCEDE8', '#F9DCD2', '#F7CABB', '#F4B8A4', '#F1A68E', '#EE9577', '#EB8360', '#E97149', '#E65F33', '#E34E1C',
  '#FFF7E6', '#FFEECC', '#FFE6B3', '#FFDD99', '#FFD480', '#FFCC66', '#FFC550', '#FFC34D', '#FFBB33', '#FFB31A',
  '#FFFAE6', '#FFF5CC', '#FFEFB3', '#FEEA9A', '#FEE580', '#FEE067', '#FEDD5D', '#FEDB4D', '#FED634', '#FED01B',
  '#F8FAEB', '#F1F5D6', '#E9EFC2', '#E2EAAE', '#DBE59A', '#D4E085', '#CCDA71', '#C6D660', '#C5D55D', '#BED049',
  '#EDF7EE', '#DCEFDD', '#CAE7CD', '#B9DFBC', '#A7D7AB', '#95D09A', '#84C889', '#72C079', '#61B868', '#55B25C',
  '#FFFFFF', '#F2F2F2', '#E6E6E6', '#D9D9D9', '#CCCCCC', '#BFBFBF', '#B3B3B3', '#A6A6A6', '#999999', '#8C8C8C',
];

const DEFAULT_GREYS = [
  '#FFFFFF', '#E8E8E8', '#D1D1D1', '#B9B9B9', '#A2A2A2', '#8B8B8B', '#747474', '#5D5D5D', '#464646', '#2E2E2E',
];

/**
 * Extracts valid hexadecimal characters from input string
 * @param {string} value - Input string
 * @returns {string} Clean hex string (uppercase, no spaces, no #)
 */
const extractValidHexChars = (value) => value
  .trim()
  .replace('#', '')
  .toUpperCase()
  .replace(/[^0-9A-F]/g, '');

/**
 * Pads hex string to specified length with zeros
 * @param {string} hex - Hex string to pad
 * @param {number} targetLength - Target length
 * @returns {string} Padded hex string
 */
const padHexString = (hex, targetLength) => hex.padEnd(targetLength, '0');

/**
 * Doubles each character in a hex string (for 3-char shorthand expansion)
 * @param {string} hex - 3-character hex string
 * @returns {string} 6-character expanded hex string
 */
const expandShorthandHex = (hex) => hex.split('').map((char) => char.repeat(2)).join('');

/**
 * Duplicates single character and pads to create valid hex
 * @param {string} char - Single hex character
 * @returns {string} 6-character hex string
 */
const expandSingleChar = (char) => {
  const doubled = char.repeat(2);
  return padHexString(doubled, 6);
};

/**
 * Truncates hex string to maximum 6 characters
 * @param {string} hex - Hex string to truncate
 * @returns {string} Truncated hex string (max 6 chars)
 */
const truncateHex = (hex) => hex.substring(0, 6);

/**
 * Formats hex string based on its length using appropriate strategy
 * @param {string} cleanHex - Clean hex string (valid chars only)
 * @returns {string} Formatted 6-character hex color with # prefix
 */
const formatHexByLength = (cleanHex) => {
  const { length } = cleanHex;

  switch (length) {
    case 1:
      return `#${expandSingleChar(cleanHex)}`;
    case 2:
      return `#${padHexString(cleanHex, 6)}`;
    case 3:
      return `#${expandShorthandHex(cleanHex)}`;
    case 4:
    case 5:
      return `#${padHexString(cleanHex, 6)}`;
    case 6:
      return `#${cleanHex}`;
    default:
      return `#${truncateHex(cleanHex)}`;
  }
};

/**
 * Extract valid hex characters and format to a valid hex color
 * @param {string} value - Input color value
 * @returns {string} Formatted hex color from valid characters, or original value if no valid hex characters found
 */
const formatHexColor = (value) => {
  if (!value) return value;

  const cleanHex = extractValidHexChars(value);

  // If no valid hex characters found, return original value
  if (!cleanHex) return value;

  return formatHexByLength(cleanHex);
};

/**
 * Calculate contrast text color based on background color
 * @param {string} backgroundColor - Background color in hex format
 * @returns {string} Either 'white' or 'black' for optimal contrast
 */
/**
 * Calculate contrast text color based on background color
 * @param {string} backgroundColor - Background color in hex format
 * @returns {string} Either 'white' or 'black' for optimal contrast
 */
const getContrastTextColor = (backgroundColor) => {
  if (!backgroundColor) return 'inherit';

  const color = backgroundColor.replace('#', '');

  // Handle invalid hex colors
  if (color.length < 6) return 'inherit';

  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);

  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > 0.5 ? 'black' : 'white';
};

/**
 * Custom hook for color field logic
 * @param {object} props
 * @param {string} [props.value] - Current color value
 * @param {(value: string) => void} [props.onChange] - Change handler
 * @param {boolean} [props.disabled] - Whether input is disabled
 * @param {boolean} [props.initialOpen] - Whether to open popover initially (for direct edit)
 * @returns {{
 *  isOpen: boolean,
 *  setIsOpen: (isOpen: boolean) => void,
 *  isEmpty: boolean,
 *  inputRef: React.RefObject<HTMLInputElement>,
 *  DEFAULT_COLORS: string[],
 *  DEFAULT_GREYS: string[],
 *  handleColorSelect: (color: string) => void,
 *  handleInputClick: () => void,
 *  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
 *  handleInputBlur: (e: React.FocusEvent<HTMLInputElement>) => void,
 *  getInputStyle: () => { backgroundColor: string, color: string },
 *  formatHexColor: (value: string) => string,
 *  getContrastTextColor: (backgroundColor: string) => string,
 * }} Hook state and handlers
 */
const useColorField = ({
  value = '',
  onChange,
  disabled = false,
  initialOpen = false,
}) => {
  const [isOpen, setIsOpen] = useState(initialOpen);
  const inputRef = useRef(null);

  const isEmpty = !value || value === '';

  useEffect(() => {
    if (initialOpen && !disabled) {
      setIsOpen(true);
    }
  }, [initialOpen, disabled]);

  /**
   * Handle color selection from palette
   * @param {string} color
   */
  const handleColorSelect = (color) => {
    if (onChange) {
      onChange(color);
    }
    setIsOpen(false);
    // Delay blur to allow React state update to complete
    // This is especially important for direct edit where onChange updates editValue
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.blur();
      }
    }, 0);
  };

  /**
   * Handle input click to open popover
   * @returns {void}
   */
  const handleInputClick = () => {
    if (!disabled && inputRef.current) {
      setIsOpen(true);
      setTimeout(() => {
        inputRef.current.focus();
      }, 0);
    }
  };

  /**
   * Handle input value change
   * @param {React.ChangeEvent<HTMLInputElement>} e
   * @returns {void}
   */
  const handleInputChange = (e) => {
    if (onChange) {
      onChange(e.target.value);
    }
  };

  /**
   * Handle input blur to format the color value
   * @param {React.FocusEvent<HTMLInputElement>} e
   * @returns {void}
   */
  const handleInputBlur = (e) => {
    if (onChange && e.target.value) {
      const formattedValue = formatHexColor(e.target.value);
      if (formattedValue !== e.target.value) {
        onChange(formattedValue);
      }
    }
  };

  /**
   * Get input style based on current value
   * @returns {{ backgroundColor: string, color: string }} Style object for input
   */
  const getInputStyle = () => ({
    backgroundColor: value || 'transparent',
    color: value ? getContrastTextColor(value) : 'black',
  });

  return {
    // State
    isOpen,
    setIsOpen,
    isEmpty,
    inputRef,

    // Data
    DEFAULT_COLORS,
    DEFAULT_GREYS,

    // Handlers
    handleColorSelect,
    handleInputClick,
    handleInputChange,
    handleInputBlur,

    // Utilities
    getInputStyle,
    formatHexColor,
    getContrastTextColor,
  };
};

// Export helper functions for testing if needed
export {
  formatHexColor,
  extractValidHexChars,
  formatHexByLength,
  expandShorthandHex,
  padHexString,
  getContrastTextColor,
};
export default useColorField;
